import { useCallback, useEffect, useState } from 'react';
import {
	extractCreditRulesError,
	getCreditAssignment,
	getCreditAssignments,
	getCreditRulesCatalog,
	getCreditTerms,
	previewDueDate,
	resolveDueDate,
} from '../services';
import {
	ICreditAssignmentData,
	ICreditAssignmentsData,
	ICreditRulesCatalog,
	ICreditTerm,
	ICreditTermPayload,
	IResolveDueDateData,
} from '../types';

interface HookState<T> {
	data: T | null;
	loading: boolean;
	error: string | null;
	refetch: () => void;
}

/**
 * Catálogo de FORMAS de regla (qué tipos existen y qué parámetros pide cada
 * uno). Lo sirve el backend para que el formulario no hardcodee las etiquetas
 * y no se desincronice si mañana se agrega un tipo de regla.
 */
export function useCreditRulesCatalog(): { data: ICreditRulesCatalog | null; loading: boolean } {
	const [data, setData] = useState<ICreditRulesCatalog | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		getCreditRulesCatalog()
			.then((c) => !cancelled && setData(c))
			.catch(() => !cancelled && setData(null))
			.finally(() => !cancelled && setLoading(false));
		return () => {
			cancelled = true;
		};
	}, []);

	return { data, loading };
}

/**
 * Preview en vivo mientras se arma una condición en el formulario: "si facturás
 * hoy con esto, vence el ...". Debounce para no pegarle al API por tecla.
 * Devuelve también el error de validación, que es el feedback más útil: dice
 * exactamente qué parámetro falta.
 */
export function useCreditTermPreview(
	payload: ICreditTermPayload | null,
	fechaFactura?: string,
): { data: IResolveDueDateData | null; error: string | null; loading: boolean } {
	const [data, setData] = useState<IResolveDueDateData | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const key = payload ? JSON.stringify(payload) : '';

	useEffect(() => {
		if (!key) {
			setData(null);
			setError(null);
			return;
		}
		let cancelled = false;
		setLoading(true);
		const timer = setTimeout(() => {
			previewDueDate({ ...(JSON.parse(key) as ICreditTermPayload), fechaFactura })
				.then((d) => {
					if (cancelled) return;
					setData(d);
					setError(null);
				})
				.catch((err) => {
					if (cancelled) return;
					setData(null);
					setError(extractCreditRulesError(err));
				})
				.finally(() => {
					if (!cancelled) setLoading(false);
				});
		}, 400);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [key, fechaFactura]);

	return { data, error, loading };
}

/** Catálogo de condiciones (para el selector y el ABM). */
export function useCreditTerms(includeDisabled = false): HookState<ICreditTerm[]> {
	const [data, setData] = useState<ICreditTerm[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [tick, setTick] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		getCreditTerms(includeDisabled)
			.then((terms) => {
				if (!cancelled) setData(terms);
			})
			.catch((err) => {
				if (!cancelled) setError(extractCreditRulesError(err));
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [includeDisabled, tick]);

	return { data, loading, error, refetch: useCallback(() => setTick((t) => t + 1), []) };
}

/** Condición vigente de un cliente. */
export function useCreditAssignment(passengerId: string | undefined): HookState<ICreditAssignmentData> {
	const [data, setData] = useState<ICreditAssignmentData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [tick, setTick] = useState(0);

	useEffect(() => {
		if (!passengerId) {
			setData(null);
			setLoading(false);
			return;
		}
		let cancelled = false;
		setLoading(true);
		setError(null);
		getCreditAssignment(passengerId)
			.then((d) => {
				if (!cancelled) setData(d);
			})
			.catch((err) => {
				if (!cancelled) setError(extractCreditRulesError(err));
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [passengerId, tick]);

	return { data, loading, error, refetch: useCallback(() => setTick((t) => t + 1), []) };
}

/** Listado de clientes con condición vigente (pantalla de gestión). */
export function useCreditAssignments(filters: {
	agencyId?: number;
	termId?: number;
	search?: string;
	limit?: number;
	offset?: number;
}): HookState<ICreditAssignmentsData> {
	const { agencyId, termId, search, limit, offset } = filters;
	const [data, setData] = useState<ICreditAssignmentsData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [tick, setTick] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		getCreditAssignments({ agencyId, termId, search, limit, offset })
			.then((d) => {
				if (!cancelled) setData(d);
			})
			.catch((err) => {
				if (!cancelled) setError(extractCreditRulesError(err));
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [agencyId, termId, search, limit, offset, tick]);

	return { data, loading, error, refetch: useCallback(() => setTick((t) => t + 1), []) };
}

/**
 * Vencimiento sugerido para una factura. Es el hook que consume el drawer:
 * devuelve la fecha, la condición aplicada y el desglose para poder explicarla.
 * Silencioso ante errores: si falla, el drawer sigue funcionando a mano.
 */
export function useResolvedDueDate(params: {
	passengerId: string | undefined;
	fechaFactura?: string;
	fechaEmisionServicio?: string;
	fechaSalidaPrimerServicio?: string;
	enabled?: boolean;
}): { data: IResolveDueDateData | null; loading: boolean } {
	const { passengerId, fechaFactura, fechaEmisionServicio, fechaSalidaPrimerServicio, enabled = true } = params;
	const [data, setData] = useState<IResolveDueDateData | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!passengerId || !enabled) {
			setData(null);
			return;
		}
		let cancelled = false;
		setLoading(true);
		resolveDueDate({
			passengerId,
			fechaFactura,
			fechaEmisionServicio,
			fechaSalidaPrimerServicio,
		})
			.then((d) => {
				if (!cancelled) setData(d);
			})
			.catch(() => {
				// El vencimiento se completa a mano: no rompemos la facturación
				// porque la condición no se pudo resolver.
				if (!cancelled) setData(null);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [passengerId, fechaFactura, fechaEmisionServicio, fechaSalidaPrimerServicio, enabled]);

	return { data, loading };
}
